import clsx from "clsx"
import { memo, useMemo } from "react"

import useNavigationLinks, { NavigationLink } from "@hooks/useNavigationLinks"
import useUser from "@hooks/useUser"

import filterUserLinks from "@utils/filterUserLinks"
import generateRandomKey from "@utils/generateRandomKey"

import AccessCheck from "@particles/AccessCheck/AccessCheck"

import { ScrollableContainer } from "@atoms"

import SideBarItem from "@organisms/SideBar/components/SideBarItem/SideBarItem"
import SideBarItemGroup from "@organisms/SideBar/components/SideBarItemGroup/SideBarItemGroup"
import SideBarProfileButton from "@organisms/SideBar/components/SideBarProfileButton/SideBarProfileButton"
import SideBarWorkspaceButton from "@organisms/SideBar/components/SideBarWorkspaceButton/SideBarWorkspaceButton"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import HelpCenter from "./HelpCenter/HelpCenter"
import styles from "./MainSideBar.module.scss"
import { MainSideBarProps } from "./MainSideBar.types"
import SideBarCreateButton from "./SideBarCreateButton/SideBarCreateButton"
import SideBarSearchButton from "./SideBarSearchButton/SideBarSearchButton"
import WhatsNew from "./WhatsNew/WhatsNew"

function MainSideBar(props: MainSideBarProps) {
    const { hideCreateButton = false } = props
    const { user } = useUser()

    const { home, calendar, estimates, jobs, invoices, purchase_orders, bills, vendors, clients, pricebook, reports } =
        useNavigationLinks()

    const links: NavigationLink[] = [
        home,
        calendar,
        estimates,
        jobs,
        invoices,
        purchase_orders,
        bills,
        vendors,
        clients,
        pricebook,
        reports,
    ]

    const filteredLinks = useMemo(() => filterUserLinks(links, user), [links, user])

    return (
        <nav className={clsx(styles.base, "jsMainSideBar")}>
            <div className={styles.header}>
                <SideBarWorkspaceButton />

                <SideBarProfileButton />
            </div>
            <div className={styles.searchInput}>
                <AccessCheck
                    permissions={{
                        estimates_list_permission: PERMISSION_LEVEL.FULL,
                        jobs_list_permission: PERMISSION_LEVEL.FULL,
                        invoices_list_permission: PERMISSION_LEVEL.FULL,
                        purchase_orders_list_permission: PERMISSION_LEVEL.FULL,
                        bills_list_permission: PERMISSION_LEVEL.FULL,
                        vendors_list_permission: PERMISSION_LEVEL.FULL,
                        clients_list_permission: PERMISSION_LEVEL.FULL,
                        pricebook_list_permission: PERMISSION_LEVEL.FULL,
                    }}
                >
                    <SideBarSearchButton />
                </AccessCheck>
            </div>

            <ScrollableContainer className={clsx(styles.sideBarItems)} scrollClassName={styles.scrolling}>
                <div className={styles.primaryNavigation}>
                    {filteredLinks.map((link) => {
                        const sublinkCount = filterUserLinks(link.subLinks, user).length
                        if (sublinkCount > 1) {
                            return (
                                <SideBarItemGroup
                                    key={generateRandomKey()}
                                    header={link}
                                    items={filterUserLinks(link.subLinks, user)}
                                />
                            )
                        } else if (sublinkCount === 1) {
                            const sublink = filterUserLinks(link.subLinks, user)[0]
                            return (
                                <SideBarItem
                                    key={generateRandomKey()}
                                    label={sublink.label}
                                    path={sublink.path}
                                    icon={link.icon}
                                    iconActive={link.iconActive}
                                />
                            )
                        } else {
                            return (
                                <SideBarItem
                                    key={generateRandomKey()}
                                    label={link.label}
                                    path={link.path}
                                    icon={link.icon}
                                    iconActive={link.iconActive}
                                />
                            )
                        }
                    })}
                </div>
                <div className={styles.secondaryNavigation}>
                    <HelpCenter />
                    <WhatsNew />
                </div>
            </ScrollableContainer>
            {!hideCreateButton && (
                <AccessCheck
                    permissions={{
                        estimates_create_permission: PERMISSION_LEVEL.RESTRICTED,
                        jobs_create_permission: PERMISSION_LEVEL.RESTRICTED,
                        invoices_create_permission: PERMISSION_LEVEL.RESTRICTED,
                        purchase_orders_create_permission: PERMISSION_LEVEL.RESTRICTED,
                        bills_create_permission: PERMISSION_LEVEL.RESTRICTED,
                        vendors_create_permission: PERMISSION_LEVEL.FULL,
                        clients_create_permission: PERMISSION_LEVEL.FULL,
                        pricebook_create_permission: PERMISSION_LEVEL.FULL,
                    }}
                >
                    <SideBarCreateButton />
                </AccessCheck>
            )}
        </nav>
    )
}

export default memo(MainSideBar)
